<script setup lang="ts">
import { computed } from "vue";

import { addReactiveAlmMapping } from "@/backend/http/userAlmMapping";
import MenuList from "@/components/ui/MenuList/MenuList.vue";
import MenuListHeader from "@/components/ui/MenuList/MenuListHeader.vue";
import MenuListItem from "@/components/ui/MenuList/MenuListItem.vue";
import MenuListItems from "@/components/ui/MenuList/MenuListItems.vue";
import MenuListLoadingBar from "@/components/ui/MenuList/MenuListLoadingBar.vue";
import MenuListSearch from "@/components/ui/MenuList/MenuListSearch.vue";
import MenuListTitle from "@/components/ui/MenuList/MenuListTitle.vue";
import { useUserSearch } from "@/composables/useUserSearch";
import type { AuthUser } from "@/model/user";
import { useTeamStore } from "@/store/team";

import NoUserEntry from "./NoUserEntry.vue";
import UserEntry from "./UserEntry.vue";

const props = defineProps<{ selected: AuthUser | null; title?: string }>();

const emit = defineEmits<{ select: [user: AuthUser | null] }>();

const { users, searchQuery, isSearching } = useUserSearch({
  team: useTeamStore().current,
  excludeUserIds: computed(() => (props.selected ? [props.selected.id] : [])),
  includeCurrentUser: true,
  addAlmMapping: true,
});

// Add ALM mapping to selected user for display
const selected = computed(() =>
  props.selected ? addReactiveAlmMapping({ ...props.selected, foreign: false }) : null,
);

function select(user: AuthUser | null) {
  emit("select", user);
}
</script>

<template>
  <MenuList selectable>
    <MenuListHeader>
      <MenuListTitle>{{ title || $t("general.assignee") }}</MenuListTitle>
      <MenuListSearch
        :placeholder="$t('action.searchUsers')"
        :icon-title="$t('label.userSelector.searchTitle')"
        :results-count="users.length"
        @search="searchQuery = $event"
      />
    </MenuListHeader>
    <MenuListItems>
      <UserEntry v-if="selected" :user="selected" active />
      <NoUserEntry v-if="selected" @select="select" />
      <UserEntry
        v-for="user in users"
        :key="user.id"
        :user
        :active="selected?.id === user.id"
        @select="select"
      />
      <MenuListItem v-if="!users.length" static>
        {{ $t("searchResults.noResults") }}
      </MenuListItem>
    </MenuListItems>
    <Transition name="loading-bar">
      <MenuListLoadingBar v-if="isSearching" />
    </Transition>
  </MenuList>
</template>
